<template>
    <div>
        <PageDescriptionEditor page-title="Press"
            default-description="Stay up to date with the latest news and announcements from <i>Barry & Bonna Publishing</i>." />

        <div class="mb-6">
            <h2 class="font-header text-[3rem] font-bold text-primary">Press Management</h2>
        </div>

        <div v-if="showForm" class="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 class="font-header text-[1.5rem] font-bold mb-4">{{ editingPress ? 'Edit' : 'Add' }} Press Release</h3>
            <form @submit.prevent="savePress" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                    <div class="col-span-2">
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Title</label>
                        <input v-model="formData.title" type="text" required
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div class="col-span-2">
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Category</label>
                        <input v-model="formData.category" type="text" placeholder="e.g., New Release, Award, Event"
                            class="w-full px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                    </div>
                    <div class="col-span-2">
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Image (Optional)</label>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <input ref="fileInput" type="file" accept="image/*,.heic,.heif"
                                    @change="handleFileSelect" class="hidden" />
                                <button type="button" @click="() => fileInput?.click()"
                                    class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-body text-[1.125rem] leading-[1.75]">
                                    Upload Image
                                </button>
                                <input v-model="formData.image" type="url" placeholder="Or enter image URL"
                                    class="flex-1 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
                            </div>
                            <div v-if="formData.image && !uploading" class="mt-2 space-y-2">
                                <div class="flex flex-wrap items-center gap-2">
                                    <button type="button" @click="loadImageForCropping"
                                        :disabled="showCropModal || loadingCropImage"
                                        :class="['px-4 py-2 rounded-md transition-colors font-body text-[1.125rem] leading-[1.75]', (showCropModal || loadingCropImage) ? 'bg-gray-400 text-gray-600 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800']">
                                        {{ loadingCropImage ? 'Loading...' : 'Crop Image' }}
                                    </button>
                                    <button type="button" @click="clearImage"
                                        class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-body text-[1.125rem] leading-[1.75]">
                                        Remove Image
                                    </button>
                                </div>
                                <div class="w-full max-w-full overflow-hidden">
                                    <div :style="getImageStyle()"
                                        class="border border-creme-300 overflow-hidden mx-auto">
                                        <img :src="formData.image" alt="Preview" class="w-full h-full object-cover" />
                                    </div>
                                </div>
                                <div v-if="formData.image_crop_dimensions && (formData.image_crop_dimensions.width > 0 || formData.image_crop_dimensions.height > 0)"
                                    class="space-y-2">
                                    <div class="font-body text-[1.125rem] leading-[1.75] text-gray-600">
                                        Dimensions: {{ Math.round(formData.image_crop_dimensions.width) }} × {{
                                            Math.round(formData.image_crop_dimensions.height) }}px
                                    </div>
                                    <div class="grid grid-cols-2 gap-2">
                                        <div>
                                            <label class="block text-xs font-header mb-1">Width (px)</label>
                                            <input v-model.number="formData.image_crop_dimensions.width" type="number"
                                                step="0.1" min="0" @input="updatePreviewDimensions"
                                                class="w-full px-2 py-1 text-[1.125rem] bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                                        </div>
                                        <div>
                                            <label class="block text-xs font-header mb-1">Height (px)</label>
                                            <input v-model.number="formData.image_crop_dimensions.height" type="number"
                                                step="0.1" min="0" @input="updatePreviewDimensions"
                                                class="w-full px-2 py-1 text-[1.125rem] bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-body" />
                                        </div>
                                    </div>
                                    <div class="p-3 bg-creme-100 rounded-md border border-creme-300">
                                        <p class="font-header text-[1.5rem] font-bold text-primary mb-2">True-to-Scale
                                            Preview:</p>
                                        <div class="flex items-center justify-center">
                                            <div :style="getTrueToScalePreviewStyle()"
                                                class="border-2 border-gray-400 bg-white overflow-hidden shadow-sm">
                                                <img :src="formData.image" alt="True scale preview"
                                                    class="w-full h-full object-cover" />
                                            </div>
                                        </div>
                                        <p
                                            class="font-body text-[1.125rem] leading-[1.75] text-gray-500 mt-2 text-center">
                                            This is how the image will appear at actual size</p>
                                    </div>
                                </div>
                            </div>
                            <div v-if="uploading" class="text-sm text-gray-600 font-marketing">
                                Uploading...
                            </div>
                        </div>
                    </div>
                    <div class="col-span-2">
                        <label class="block font-body text-[1.125rem] leading-[1.75] mb-1">Content</label>
                        <div class="border border-gray-300 rounded-md">
                            <RichTextEditor ref="contentEditor" :content="formData.content"
                                @update:content="formData.content = $event"
                                placeholder="Write your press release content here..." />
                        </div>
                    </div>
                </div>
                <div class="flex gap-2">
                    <button type="submit"
                        class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem]">
                        {{ editingPress ? 'Update' : 'Create' }}
                    </button>
                    <button type="button" @click="cancelForm"
                        class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-header text-[1.125rem]">
                        Cancel
                    </button>
                </div>
            </form>
        </div>

        <div class="mb-4 flex justify-end">
            <button @click="showForm = true; editingPress = null"
                class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem]">
                Add New Press Release
            </button>
        </div>

        <div class="bg-white rounded-lg shadow-md overflow-hidden">
            <table class="w-full">
                <thead class="bg-green-700 text-white">
                    <tr>
                        <th class="px-4 py-3 text-left font-header">Title</th>
                        <th class="px-4 py-3 text-left font-header">Category</th>
                        <th class="px-4 py-3 text-left font-header">Published Date</th>
                        <th class="px-4 py-3 text-left font-header">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="press in pressItems" :key="press.id" class="border-b border-creme-200 hover:bg-creme-50">
                        <td class="px-4 py-3 font-header text-gray-900">{{ press.title }}</td>
                        <td class="px-4 py-3 font-header text-gray-900">{{ press.category || '-' }}</td>
                        <td class="px-4 py-3 font-header text-[1.125rem] text-gray-900">{{ press.published_at ?
                            formatDateShort(press.published_at) : '-' }}</td>
                        <td class="px-4 py-3">
                            <button @click="editPress(press)"
                                class="text-green-700 hover:text-green-800 mr-3 font-header text-[1.125rem]">Edit</button>
                            <button @click="deletePress(press.id)"
                                class="text-red-600 hover:text-red-700 font-header text-[1.125rem]">Delete</button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="loading" class="p-4 text-center">
                <Loader size="medium" />
            </div>
            <div v-else-if="pressItems.length === 0" class="p-8 text-center text-gray-500 font-marketing">
                <span class="font-body text-body">No press releases found</span>
            </div>
            <div v-if="pagination && pagination.total_pages > 1"
                class="p-4 flex justify-between items-center border-t border-creme-200">
                <button @click="loadPress(currentPage - 1)" :disabled="!pagination.prev_page"
                    :class="['px-4 py-2 rounded-md font-header text-[1.125rem]', pagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Previous
                </button>
                <span class="font-header text-gray-700">
                    Page {{ pagination.page }} of {{ pagination.total_pages }} ({{ pagination.total }} total)
                </span>
                <button @click="loadPress(currentPage + 1)" :disabled="!pagination.next_page"
                    :class="['px-4 py-2 rounded-md font-header text-[1.125rem]', pagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                    Next
                </button>
            </div>
        </div>

        <BaseModal v-model="showCropModal" content-class="max-w-4xl max-h-[90vh]" @close="closeCropModal">
            <div class="mb-4">
                <h3 class="font-header text-[1.5rem] font-bold text-primary">Crop Press Image</h3>
            </div>
            <div v-if="loadingCropImage" class="mb-4 flex items-center justify-center" style="height: 700px;">
                <Loader size="large" />
            </div>
            <div v-else-if="cropImageSrc" class="mb-4 relative" style="height: 700px; width: 100%;">
                <Cropper :key="`cropper-${cropModalKey}-${cropImageSrc}`" ref="cropper" :src="cropImageSrc"
                    :default-boundaries="'fill'" :stencil-props="{
                        aspectRatio: null
                    }" @change="onCropChange" @ready="onCropperReady" style="width: 100%; height: 100%;" />
            </div>
            <div v-else class="mb-4 flex items-center justify-center" style="height: 700px;">
                <p class="text-gray-500 font-body text-body">No image selected</p>
            </div>

            <div v-if="cropImageSrc && canZoomOut" class="mb-4 flex justify-center">
                <button @click="fitImageToCrop"
                    class="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-header font-bold text-[1.125rem] shadow-sm flex items-center gap-2"
                    title="Fit image to crop area (zoom out)">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                    </svg>
                    Fit to Crop
                </button>
            </div>

            <div v-if="cropResult && cropResult.coordinates"
                class="mb-6 p-4 bg-creme-100 rounded-lg border border-creme-300">
                <h4 class="text-sm font-header font-bold text-primary mb-3">Preview</h4>
                <div class="flex items-center justify-center">
                    <div :style="getPreviewPressStyle(cropResult.coordinates)"
                        class="bg-white border-2 border-gray-300 shadow-md overflow-hidden">
                        <img v-if="previewImageUrl" :src="previewImageUrl" alt="Preview"
                            class="w-full h-full object-cover" />
                    </div>
                </div>
                <p class="text-xs text-gray-500 font-body text-body mt-2 text-center">{{
                    Math.round(cropResult.coordinates.width)
                }} × {{ Math.round(cropResult.coordinates.height) }}px</p>
            </div>

            <div class="flex gap-2 justify-end">
                <button @click="closeCropModal"
                    class="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-header text-[1.125rem]">
                    Cancel
                </button>
                <button @click="applyCrop"
                    class="px-6 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 transition-colors font-header text-[1.125rem]">
                    Apply Crop
                </button>
            </div>
        </BaseModal>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { adminService } from '../../services/admin'
import { API_BASE_URL } from '../../config'
import { useScrollToTop } from '../../composables/useScrollToTop'

const { scrollToTop } = useScrollToTop()
import PageDescriptionEditor from './PageDescriptionEditor.vue'
import Loader from '../Loader.vue'
import BaseModal from './BaseModal.vue'
import { useLoadingState } from '../../composables/useLoadingState'
import RichTextEditor from '../common/RichTextEditor.vue'
import { formatDateShort } from '../../utils/date'
import { convertLegacyQuillContent } from '../../utils/quillConfig'

const pressItems = ref([])
const showForm = ref(false)
const editingPress = ref(null)
const currentPage = ref(1)
const limit = ref(10)
const pagination = ref(null)
const { loading, setLoading } = useLoadingState(false)

const formData = ref({
    title: '',
    content: convertLegacyQuillContent(''),
    image: '',
    image_crop_dimensions: null,
    category: ''
})
const contentEditor = ref(null)
const fileInput = ref(null)
const uploading = ref(false)
const showCropModal = ref(false)
const cropper = ref(null)
const cropImageSrc = ref('')
const cropResult = ref(null)
const selectedFile = ref(null)
const previewImageUrl = ref('')
const canZoomOut = ref(false)
const imageNaturalSize = ref({ width: 0, height: 0 })
const imagePreview = ref('')
const loadingCropImage = ref(false)
const cropModalKey = ref(0)
const Math = window.Math

const loadPress = async (page = 1) => {
    setLoading(true)
    try {
        const response = await adminService.getPress(page, limit.value)
        if (response.data && response.pagination) {
            pressItems.value = response.data
            pagination.value = response.pagination
            currentPage.value = page
        } else {
            pressItems.value = Array.isArray(response) ? response : []
            pagination.value = null
        }
    } catch (error) {
        console.error('Failed to load press:', error)
        pressItems.value = []
        pagination.value = null
    } finally {
        setLoading(false)
    }
}

const handleFileSelect = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    const isImage = file.type.startsWith('image/') ||
        file.name.toLowerCase().endsWith('.heic') ||
        file.name.toLowerCase().endsWith('.heif')
    if (!isImage) {
        alert('Please select an image file')
        return
    }

    if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB')
        return
    }

    if (showCropModal.value || loadingCropImage.value) {
        return
    }

    let blobUrl
    try {
        const { convertHeicForUpload, convertHeicForPreview } = await import('../../utils/heicConverter')
        const convertedFile = await convertHeicForUpload(file)
        selectedFile.value = convertedFile
        blobUrl = await convertHeicForPreview(file)
    } catch (error) {
        console.error('Failed to convert HEIC:', error)
        selectedFile.value = file
        blobUrl = URL.createObjectURL(file)
    }

    imagePreview.value = blobUrl
    formData.value.image = blobUrl
}

const loadImageForCropping = async () => {
    if (showCropModal.value || loadingCropImage.value) {
        return
    }

    const imageUrl = imagePreview.value || formData.value.image
    if (!imageUrl) {
        alert('Please upload an image or enter an image URL first')
        return
    }

    loadingCropImage.value = true
    cropResult.value = null
    previewImageUrl.value = ''
    canZoomOut.value = false
    imageNaturalSize.value = { width: 0, height: 0 }

    let blobUrl = imagePreview.value

    try {
        if (!blobUrl || !blobUrl.startsWith('blob:')) {
            let blob
            if (imageUrl.includes('storage.googleapis.com')) {
                const proxyUrl = `${API_BASE_URL}/api/admin/proxy/image?url=${encodeURIComponent(imageUrl)}`
                const response = await fetch(proxyUrl, {
                    credentials: 'include'
                })
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            } else {
                const response = await fetch(imageUrl)
                if (!response.ok) {
                    throw new Error('Failed to load image')
                }
                blob = await response.blob()
            }

            blobUrl = URL.createObjectURL(blob)
            imagePreview.value = blobUrl
        }

        cropImageSrc.value = blobUrl
        selectedFile.value = null
        cropModalKey.value++
        showCropModal.value = true
        await nextTick()
    } catch (error) {
        console.error('Failed to load image for cropping:', error)
        alert('Failed to load image. Please check the image URL or try uploading the image again.')
    } finally {
        loadingCropImage.value = false
    }
}

const closeCropModal = () => {
    const fileToUpload = selectedFile.value

    if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
        URL.revokeObjectURL(cropImageSrc.value)
    }
    cropImageSrc.value = ''
    cropResult.value = null
    previewImageUrl.value = ''
    showCropModal.value = false
    canZoomOut.value = false
    imageNaturalSize.value = { width: 0, height: 0 }
    loadingCropImage.value = false
    cropper.value = null

    if (fileToUpload && !formData.value.image) {
        imagePreview.value = ''
        if (fileInput.value) {
            fileInput.value.value = ''
        }
    } else if (!formData.value.image) {
        imagePreview.value = ''
    }

    selectedFile.value = null
}

const onCropperReady = () => {
    if (cropper.value) {
        const result = cropper.value.getResult()
        if (result?.imageSize) {
            imageNaturalSize.value = {
                width: result.imageSize.width,
                height: result.imageSize.height
            }
        }
        if (result) {
            cropResult.value = result
            updatePreview()
        }
    }
}

const onCropChange = ({ coordinates, canvas, visibleArea }) => {
    cropResult.value = { coordinates, canvas, visibleArea }
    if (canvas) {
        previewImageUrl.value = canvas.toDataURL('image/jpeg', 0.95)
    }

    if (cropper.value && coordinates) {
        const cropWidth = coordinates.width
        const cropHeight = coordinates.height

        if (cropWidth && cropHeight) {
            const imageWidth = imageNaturalSize.value.width || visibleArea?.width || 0
            const imageHeight = imageNaturalSize.value.height || visibleArea?.height || 0

            if (imageWidth && imageHeight) {
                const needsZoomOut = imageWidth > cropWidth || imageHeight > cropHeight
                canZoomOut.value = needsZoomOut
            }
        }
    }

    updatePreview()
}

const updatePreview = async () => {
    if (!cropper.value || !cropResult.value) return

    try {
        const canvas = cropResult.value.canvas
        if (canvas) {
            previewImageUrl.value = canvas.toDataURL()
        }
    } catch (error) {
        console.error('Failed to update preview:', error)
    }
}

const applyCrop = async () => {
    if (!cropper.value || !cropResult.value) {
        alert('Please select a crop area by adjusting the crop box')
        return
    }

    try {
        const { coordinates, canvas } = cropResult.value

        if (!coordinates || !coordinates.width || !coordinates.height) {
            alert('Please select a crop area')
            return
        }

        formData.value.image_crop_dimensions = {
            width: coordinates.width,
            height: coordinates.height
        }

        if (canvas) {
            uploading.value = true

            canvas.toBlob(async (blob) => {
                if (!blob) {
                    alert('Failed to create cropped image')
                    uploading.value = false
                    return
                }

                try {
                    const currentImageUrl = formData.value.image || imagePreview.value
                    let fileName = selectedFile.value?.name || (currentImageUrl ? currentImageUrl.split('/').pop().split('?')[0] : 'cropped-image.jpg')
                    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '')
                    fileName = `${nameWithoutExt}.jpg`

                    const formDataUpload = new FormData()
                    formDataUpload.append('file', blob, fileName)

                    const uploadedUrl = await adminService.uploadPressImage(formDataUpload)
                    if (uploadedUrl) {
                        formData.value.image = uploadedUrl
                        imagePreview.value = uploadedUrl

                        if (cropImageSrc.value && cropImageSrc.value.startsWith('blob:')) {
                            URL.revokeObjectURL(cropImageSrc.value)
                        }

                        uploading.value = false
                        showCropModal.value = false
                        selectedFile.value = null
                        cropResult.value = null
                    } else {
                        throw new Error('No URL returned from server')
                    }
                } catch (error) {
                    console.error('Failed to upload cropped image:', error)
                    const errorMessage = error.response?.data?.error || error.message || 'Failed to upload cropped image. Please try again.'
                    alert(errorMessage)
                    uploading.value = false
                }
            }, 'image/jpeg', 0.95)
        } else {
            alert('Failed to get cropped canvas')
        }
    } catch (error) {
        console.error('Failed to apply crop:', error)
        alert('Failed to apply crop. Please try again.')
    }
}

const fitImageToCrop = async () => {
    if (!cropper.value || !cropResult.value?.coordinates) {
        return
    }

    try {
        const coordinates = cropResult.value.coordinates
        const cropWidth = coordinates.width
        const cropHeight = coordinates.height

        if (!cropWidth || !cropHeight) return

        const imageSize = imageNaturalSize.value
        if (!imageSize.width || !imageSize.height) return

        const scaleX = cropWidth / imageSize.width
        const scaleY = cropHeight / imageSize.height
        const scale = Math.max(scaleX, scaleY)

        if (cropper.value.zoom) {
            await cropper.value.zoom(scale, { center: 'image' })
        }

        canZoomOut.value = false
    } catch (error) {
        console.error('Failed to fit image to crop:', error)
    }
}

const getPreviewPressStyle = (coordinates) => {
    if (!coordinates || !coordinates.width || !coordinates.height) {
        return { width: '400px', height: '300px' }
    }

    let displayWidth = coordinates.width
    let displayHeight = coordinates.height

    const MAX_WIDTH = 400
    if (coordinates.width > MAX_WIDTH) {
        const scale = MAX_WIDTH / coordinates.width
        displayWidth = MAX_WIDTH
        displayHeight = coordinates.height * scale
    }

    return {
        width: `${displayWidth}px`,
        height: `${displayHeight}px`
    }
}

const clearImage = () => {
    formData.value.image = ''
    formData.value.image_crop_dimensions = null
    imagePreview.value = ''
}

const getImageStyle = () => {
    if (formData.value.image_crop_dimensions &&
        formData.value.image_crop_dimensions.width > 0 &&
        formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions

        const maxWidth = 400
        let displayWidth = width
        let displayHeight = height

        if (width > maxWidth) {
            const scale = maxWidth / width
            displayWidth = maxWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`,
            objectFit: 'cover'
        }
    }
    return {
        maxWidth: '400px',
        height: '192px',
        objectFit: 'cover'
    }
}

const getTrueToScalePreviewStyle = () => {
    if (formData.value.image_crop_dimensions && formData.value.image_crop_dimensions.width > 0 && formData.value.image_crop_dimensions.height > 0) {
        const { width, height } = formData.value.image_crop_dimensions
        const maxPreviewWidth = 400
        let displayWidth = width
        let displayHeight = height

        if (width > maxPreviewWidth) {
            const scale = maxPreviewWidth / width
            displayWidth = maxPreviewWidth
            displayHeight = height * scale
        }

        return {
            width: `${displayWidth}px`,
            height: `${displayHeight}px`
        }
    }
    return {
        width: '256px',
        height: '192px'
    }
}

const updatePreviewDimensions = () => {
    if (!formData.value.image_crop_dimensions) {
        formData.value.image_crop_dimensions = { width: 0, height: 0 }
    }
}

const editPress = async (press) => {
    try {
        const fullPress = await adminService.getPressItem(press.id)
        editingPress.value = fullPress
        formData.value = {
            title: fullPress.title,
            content: convertLegacyQuillContent(fullPress.content || ''),
            image: fullPress.image || '',
            image_crop_dimensions: fullPress.image_crop_dimensions || null,
            category: fullPress.category || ''
        }
        imagePreview.value = fullPress.image || ''
        showForm.value = true
        await nextTick()
        if (contentEditor.value) {
            contentEditor.value.setHTML(formData.value.content || '')
        }
    } catch (error) {
        console.error('Failed to load press:', error)
        alert('Failed to load press details')
    }
}

const cancelForm = async () => {
    showForm.value = false
    editingPress.value = null
    imagePreview.value = ''
    uploading.value = false
    formData.value = {
        title: '',
        content: convertLegacyQuillContent(''),
        image: '',
        image_crop_dimensions: null,
        category: ''
    }
    await nextTick()
    if (contentEditor.value) {
        contentEditor.value.setHTML(convertLegacyQuillContent(''))
    }
    if (fileInput.value) {
        fileInput.value.value = ''
    }
    scrollToTop()
}

const savePress = async () => {
    try {
        // First, upload the blob image if there's a selected file that hasn't been uploaded yet
        if (selectedFile.value && imagePreview.value && imagePreview.value.startsWith('blob:')) {
            uploading.value = true
            try {
                const formDataUpload = new FormData()
                formDataUpload.append('file', selectedFile.value, selectedFile.value.name)
                const uploadedUrl = await adminService.uploadPressImage(formDataUpload)
                formData.value.image = uploadedUrl
                imagePreview.value = uploadedUrl
                selectedFile.value = null
            } catch (error) {
                console.error('Failed to upload image:', error)
                alert('Failed to upload image. Please try again.')
                uploading.value = false
                return
            } finally {
                uploading.value = false
            }
        }

        const data = {
            ...formData.value,
            image: formData.value.image || null,
            image_crop_dimensions: formData.value.image_crop_dimensions && (formData.value.image_crop_dimensions.width > 0 || formData.value.image_crop_dimensions.height > 0)
                ? formData.value.image_crop_dimensions
                : null,
            category: formData.value.category || null
        }

        if (editingPress.value) {
            await adminService.updatePress(editingPress.value.id, data)
        } else {
            await adminService.createPress(data)
        }

        await loadPress(currentPage.value)
        cancelForm()
        scrollToTop()
    } catch (error) {
        console.error('Failed to save press:', error)
        alert('Failed to save press. Please try again.')
    }
}

const deletePress = async (id) => {
    if (!confirm('Are you sure you want to delete this press release?')) return

    try {
        await adminService.deletePress(id)
        await loadPress(currentPage.value)
    } catch (error) {
        console.error('Failed to delete press:', error)
        alert('Failed to delete press. Please try again.')
    }
}

onMounted(() => {
    loadPress(1)
})
</script>

<style scoped>
:deep(.ql-editor) {
    min-height: 200px;
    line-height: 1.75;
    color: #253439;
    direction: ltr !important;
    text-align: left !important;
    unicode-bidi: embed !important;
}

:deep(.ql-container) {
    font-family: 'Manrope', sans-serif;
    direction: ltr !important;
    unicode-bidi: embed !important;
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
}

:deep(.ql-toolbar) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
    background-color: #FAFAFA;
}

:deep(.ql-editor *) {
    direction: ltr !important;
}
</style>
